import { withNavigation }         from 'components/App/withNavigation';
import { WithNavigationProps }    from 'components/App/withNavigation';
import React                      from 'react';
import { Button }                 from 'antd';
import { Popconfirm }             from 'antd';
import { computed }               from 'mobx';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { observer }               from 'mobx-react';
import ProviderModel              from 'models/ProviderModel';
import currentSession             from 'utils/currentSession';
import { PagedCollection }        from 'collections/PagedCollection';
import { messageErrorIntl }       from 'utils/messageIntl';
import { messageSuccessIntl }     from 'utils/messageIntl';
import FormattedMessageWrapper    from 'components/App/FormattedMessageWrapper';
import Search                     from 'components/App/IntlSearch';
import ListComponent              from 'components/App/ListComponent';
import handleApiError             from 'utils/handleApiError';
import { AppForm }                from 'components/App/AppForm';
import ProviderForm               from 'forms/ProviderForm';

const LogoStyle = {
  backgroundPosition: 'center',
  backgroundSize: '100% auto',
  height: '75px',
  overflow: 'hidden',
  width: '75px',
};

const openProviderForm = (model: ProviderModel, onSuccess: () => void) => {
  const title = model.id
    ? <FormattedMessageWrapper id="Modifier un prestataire" />
    : <FormattedMessageWrapper id="Ajouter un prestataire" />;

  AppForm.open(ProviderForm, { model, onSuccess }, { title });
};

@observer
class ProviderList extends React.Component<WithNavigationProps> {
  public providerCollection = new PagedCollection(ProviderModel);

  public componentDidMount() {
    if (!currentSession.isAdmin) {
      this.props.navigate('/404');
    }
  }

  public render() {
    return (
      <ListComponent
        collection={this.providerCollection}
        columns={this.columns}
        fetchData={this.fetchData}
        loading={!this.providerCollection.isLoaded || this.providerCollection.isLoading}
        onRowClick={modelId => {
          this.props.navigate(`/prestataires/${modelId}`);
        }}
        rowSelection={false}
        title={<FormattedMessageWrapper id="Prestataires" />}
        topRightWidget={this.renderTopRightWidget()}
      />
    );
  }

  public renderTopRightWidget = () => (
    <>
      <Search
        onSearch={this.onSearch}
        placeholder="Rechercher un prestataire"
        style={{ width: 200 }}
      />
      &nbsp;
      <Button
        htmlType="button"
        onClick={() => openProviderForm(new ProviderModel(), () => this.providerCollection.list())}
      >
        <FormattedMessageWrapper id="Ajouter un prestataire" />
      </Button>
    </>
  );

  private deleteProvider = async (providerId: id) => {
    const providerToDelete = new ProviderModel();
    providerToDelete.set('id', providerId);
    try {
      await providerToDelete.destroy();
      messageSuccessIntl("Prestataire supprimé !");
      this.providerCollection.list();
    } catch (error) {
      handleApiError(error);
      messageErrorIntl("Suppression impossible, assurez-vous que le prestataire a bien été supprimé de tous les salons.");
    }
  };

  private fetchData = async (pagination?: any, filters?: any, sorter?: any) => {
    this.providerCollection.setPage(pagination?.current || 1);
    this.providerCollection.filters = {};

    Object.keys(filters || {}).forEach(filterName => {
      this.providerCollection.setFilter(filterName, filters[filterName]);
    });

    this.providerCollection.sorts = {};

    if (sorter?.order) {
      this.providerCollection.setSort(sorter.field, sorter.order === 'ascend');
    }

    try {
      await this.providerCollection.list();
    } catch {
      messageErrorIntl("Une erreur s'est produite.");
    }
  };

  private onSearch = (value: string) => {
    this.fetchData(
      { current: 1 },
      { ...this.providerCollection.filters, search: value },
      this.providerCollection.sorts,
    );
  };

  @computed
  get columns() {
    return [
      {
        intlTitle: 'Nom',
        property: 'name',
        render: (model: ProviderModel) => model.providerName,
        sorter: true,
      },
      {
        intlTitle: 'Lien',
        property: 'link',
        render: (model: ProviderModel) => model.link,
        sorter: true,
      },
      {
        intlTitle: 'Email',
        property: 'email',
        render: (model: ProviderModel) => model.email,
        sorter: true,
      },
      {
        intlTitle: 'Logo',
        property: 'logo',
        render: (model: ProviderModel) => (
          <div
            style={{
              ...LogoStyle,
              backgroundImage: `url(${model.logoObjectUrl})`,
            }}
          />
        ),
      },
      {
        intlTitle: 'Actions',
        property: 'actions',
        render: (model: ProviderModel) => (
          <>
            <Button
              htmlType="button"
              onClick={event => {
                event.stopPropagation();
                openProviderForm(model, () => this.providerCollection.list());
              }}
            >
              <FormattedMessageWrapper id="Modifier" />
            </Button>
            &nbsp;
            <Popconfirm
              cancelText={<FormattedMessageWrapper id="Non" />}
              icon={<QuestionCircleOutlined style={{ color: '#e74c3c' }} />}
              okText={<FormattedMessageWrapper id="Oui" />}
              onCancel={event => event?.stopPropagation()}
              onConfirm={async event => {
                event?.stopPropagation();
                await this.deleteProvider(model.id);
              }}
              placement="left"
              title={
                <FormattedMessageWrapper id="Êtes-vous sûr de vouloir supprimer ce prestataire ?" />
              }
            >
              <Button
                htmlType="button"
                onClick={event => event.stopPropagation()}
              >
                <FormattedMessageWrapper id="Supprimer" />
              </Button>
            </Popconfirm>
          </>
        ),
      },
    ];
  }
}

const WrappedProviderList = withNavigation(ProviderList);

export default WrappedProviderList;
